import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment-timezone';
import { Modal, Form, Input } from 'antd';
import { CalendarCheck } from '@phosphor-icons/react';

import Button from '../Button';
import Typography from '../Typography';
import DatePicker from '../DatePicker';
import COLORS from '../../util/constants/colors';
import { captureAnalytics } from '../../actions/preload';
import { missingReplyAppointmentFields } from '../../containers/records/shared/referralStatusConsequences';
import './referralAppointmentPrompt.scss';

const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const FIELD_COPY = {
  app: 'appointment date',
  scheduled_provider: 'scheduled provider',
};

// What "Add and send" sends. A field that wasn't asked keeps its value on file:
// the date (in the zone its reply prints) when only the provider was asked, and
// the provider when only the date was asked. The server saves both, so a blank
// would erase it.
export const replyAppointmentPayload = ({
  appointment, missingFields, appointmentTime, providerName,
}) => {
  const zone = appointment?.appointment_time ? appointment.appointment_timezone : null;
  const time = appointmentTime
    || (appointment?.appointment_time ? moment.tz(appointment.appointment_time, zone) : null);
  return {
    appointment_time: time ? time.toISOString() : null,
    appointment_timezone: appointmentTime ? browserTimezone() : (zone || null),
    provider_name: missingFields.includes('scheduled_provider')
      ? providerName.trim()
      : (appointment?.provider_name || ''),
    expected_appointment_revision: appointment?.appointment_revision || null,
  };
};

// ENG-3859: asks, before a status change is saved, for the appointment details
// its automatic reply's template prints that the referral has none of. Both are
// optional: "Send without" saves the status and the reply goes out as it always has.
export const ReferralAppointmentPrompt = ({
  statusName, missingFields, appointment, onAdd, onSkip, onCancel,
}) => {
  const [appointmentTime, setAppointmentTime] = useState(null);
  const [providerName, setProviderName] = useState('');
  const asksDate = missingFields.includes('app');
  const asksProvider = missingFields.includes('scheduled_provider');
  const entered = Boolean(appointmentTime) || Boolean(providerName.trim());

  // A typed time is parsed in the browser's zone; the picker carries the
  // current seconds into a picked date.
  const handleTimeChange = (value) => {
    setAppointmentTime(value ? value.clone().startOf('minute') : null);
  };

  const add = () => {
    onAdd(replyAppointmentPayload({
      appointment, missingFields, appointmentTime, providerName,
    }));
  };

  const asked = missingFields.map((field) => FIELD_COPY[field]).join(' and ');

  return (
    <Modal
      className="referral-appointment-prompt"
      visible
      centered
      width={440}
      footer={null}
      closable
      onCancel={onCancel}
      maskClosable={false}
      // Above Update Referral (1000), which can open this prompt.
      zIndex={1100}
    >
      <div className="referral-appointment-prompt__icon">
        <CalendarCheck size={22} weight="fill" color={COLORS.PURPLE_PRIMARY_LITE} />
      </div>
      <Typography variant="text" size="large" bold color={COLORS.TEXT_PRIMARY_LITE}>
        {`Add the ${asked}?`}
      </Typography>
      <div className="referral-appointment-prompt__body">
        <Typography variant="text" size="small" color={COLORS.TEXT_TERTIARY_LITE}>
          {`Setting ${statusName} faxes a reply to the referring office that includes the ${asked}. `
            + `This referral doesn't have ${missingFields.length > 1 ? 'them' : 'it'} yet. Add ${missingFields.length > 1 ? 'them' : 'it'} now, or send the reply without.`}
        </Typography>
      </div>
      <Form layout="vertical" className="referral-appointment-prompt__form" onFinish={add}>
        {asksDate && (
          <Form.Item label="Appointment date and time">
            <DatePicker
              showTime={{ use12Hours: true, format: 'h:mm A' }}
              // Shown without a leading zero; a typed "08:45 AM" is accepted too.
              format={['MM/DD/YYYY h:mm A', 'MM/DD/YYYY hh:mm A']}
              value={appointmentTime}
              onChange={handleTimeChange}
              placeholder="MM/DD/YYYY h:mm AM"
              // The calendar mounts on document.body at antd's 1050; lift it above this modal.
              dropdownClassName="referral-appointment-prompt__picker"
              aria-label="Appointment date and time"
            />
          </Form.Item>
        )}
        {asksProvider && (
          <Form.Item label="Scheduled provider">
            <Input
              aria-label="Scheduled provider"
              placeholder="Dr. Name"
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
              maxLength={255}
            />
          </Form.Item>
        )}
      </Form>
      <div className="referral-appointment-prompt__actions">
        <Button type="tertiary" size="small" label="Send without" onClick={onSkip} />
        <Button type="primary" size="small" label="Add and send" onClick={add} disabled={!entered} />
      </div>
    </Modal>
  );
};

ReferralAppointmentPrompt.propTypes = {
  statusName: PropTypes.string.isRequired,
  missingFields: PropTypes.arrayOf(PropTypes.oneOf(['app', 'scheduled_provider'])).isRequired,
  appointment: PropTypes.object,
  onAdd: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ReferralAppointmentPrompt.defaultProps = {
  appointment: null,
};

const fetchScheduledAppointment = async ({ fileToken, sendToken, parentType }) => {
  const { data } = await axios.get(
    `/api/v1/records/${fileToken}/referred_document_statuses/${sendToken}?parent_type=${parentType}`,
  );
  return data?.scheduled_appointment || null;
};

// Resolves with what to send along with the status change:
//   { scheduledAppointment } — the user added details
//   {}                       — nothing to ask, or the user skipped
//   null                     — the user closed the prompt; don't change the status
// Asks only when the status's reply uses a field the referral has no value for
// and a person may set the appointment (EMR monitoring doesn't own it).
export const askForReplyAppointment = async ({
  status, fileToken, sendToken, parentType, appointment: knownAppointment, source,
}) => {
  let appointment = knownAppointment;
  if (appointment === undefined) {
    try {
      appointment = await fetchScheduledAppointment({ fileToken, sendToken, parentType });
    } catch {
      return {};
    }
  }
  const missingFields = missingReplyAppointmentFields(status, appointment);
  if (missingFields.length === 0 || !appointment?.editable) return {};

  captureAnalytics('referral_status_appointment_prompt_opened', { source, missing_fields: missingFields });

  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const finish = (result, outcome) => {
      captureAnalytics('referral_status_appointment_prompt_answered', { source, outcome, missing_fields: missingFields });
      ReactDOM.unmountComponentAtNode(container);
      container.remove();
      resolve(result);
    };
    ReactDOM.render(
      <ReferralAppointmentPrompt
        statusName={status.name}
        missingFields={missingFields}
        appointment={appointment}
        onAdd={(scheduledAppointment) => finish({ scheduledAppointment }, 'added')}
        onSkip={() => finish({}, 'skipped')}
        onCancel={() => finish(null, 'cancelled')}
      />,
      container,
    );
  });
};

export default ReferralAppointmentPrompt;
